import React, { useEffect, useState } from "react";
import { Box } from "@material-ui/core";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core/styles";
import { Provider, useDispatch } from "react-redux";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { enableMapSet } from "immer";

import {
  PremierCollectionModuleProps as VacationRentalsModuleProps,
  PremierCollectionClientAssetProps as VacationRentalsClientAssetProps,
  SessionInfo,
  CallState,
  ExperimentVariant,
} from "redmond";
import { AgentBanner } from "halifax";
import * as H from "history";
import { UserSourceProvider, isCorpTenant } from "@capone/common";
import { useMedalliaFeedback } from "b2b-base/src/components/MedalliaFeedback";
import { ExperimentsHookProvider } from "@capone/experiments";
import { IntlProvider } from "react-intl";
import useWindowEventListener from "./hooks/useWindowEventListener";
import { eventsToListen } from "./utils/events";
import * as frenchTranslations from "./lang/fr.json";
import * as englishTranslations from "./lang/en.json";
import { store } from "./store";
import AxiosInterceptors from "./components/AxiosInterceptors";
import { PortalComponentProvider } from "./components/PortalComponentProvider";
import {
  setIsFirstLaunch,
  setAgentEmail,
  fetchRewardsAccounts,
  setUserInfo,
} from "./modules/rewards/actions/actions";
import ExperimentsProvider, {
  getExperimentVariant,
  STAYS_BOOKING_EXPERIMENT,
  useExperiments,
} from "./context/experiments";
import fetchUserInfo from "./api/v0/user/fetchUserInfo";
import { fetchUserPolicies } from "b2b-base/src/api/v1/policies/fetchPolicies";
import { config } from "./api/config";
import {
  PATH_STAYS,
  PATH_VACATION_RENTALS_BOOK,
  PATH_VACATION_RENTALS_SHOP,
} from "./utils/paths";
import { VacationRentalShop } from "./modules/shop";
import { RewardsBanner } from "./modules/rewards/components";
import { VacationRentalBook } from "./modules/book";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}
const generateClassName = createGenerateClassName({
  productionPrefix: "ptVacationRentalsModule",
  seed: "ptVacationRentalsModule",
});

interface VacationRentalsClientContextType
  extends VacationRentalsClientAssetProps {
  isAgentPortal: boolean;
  showCoreHomepage?: boolean;
}

export const ClientContext = React.createContext<
  Partial<VacationRentalsClientContextType>
>({});

const App = (
  props: VacationRentalsModuleProps & {
    // No LegacyVacationRentalsBooking direct-import wrapper exists yet — VR
    // is only ever mounted as the standalone microfrontend, so this is
    // always false/unset today. Once a Legacy-style direct-import wrapper is
    // added for VR (mirroring LegacyPremierCollectionVRBook), that wrapper
    // must set this to true, the same way it's done for
    // premier-collection-module — see PortalComponentProvider's `skip` prop.
    skipPortalProvider?: boolean;
  }
) => {
  const {
    theme,
    language,
    baseHistory,
    clientAssets,
    isAgentPortal,
    experiments,
    showCoreHomepage,
    skipPortalProvider,
  } = props;

  const [activeTheme, setActiveTheme] = useState(theme);
  const [locale, setLocale] = useState(language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);
  const [policies, setPolicies] = useState(clientAssets?.policies);

  useEffect(() => {
    handleFetchUserInfo();
  }, []);

  useEffect(() => {
    if (!policies && isCorpTenant(config.TENANT)) {
      handleFetchPolicies();
    }
  }, [policies]);

  const handleFetchUserInfo = async () => {
    try {
      const userInfoResponse = await fetchUserInfo();
      setSessionInfo(userInfoResponse);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFetchPolicies = async () => {
    try {
      const policiesResponse = await fetchUserPolicies();
      setPolicies(policiesResponse.policyDetail);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  enableMapSet();

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged
  );
  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  return (
    <Provider store={store}>
      <ExperimentsProvider
        initState={{
          experiments: experiments?.experiments || [],
          trackingProperties: experiments?.trackingProperties,
          callState: CallState.NotCalled,
        }}
      >
        <ExperimentsHookProvider isLoggedIn={Boolean(sessionInfo?.csrfToken)}>
          <UserSourceProvider>
            <PortalComponentProvider skip={skipPortalProvider}>
              <Router history={baseHistory}>
                <AxiosInterceptors isAgentPortal={isAgentPortal} />
                <ClientContext.Provider
                  value={{
                    ...clientAssets,
                    sessionInfo,
                    policies,
                    isAgentPortal,
                    showCoreHomepage,
                  }}
                >
                  <div className="App">
                    <StylesProvider
                      generateClassName={generateClassName}
                      injectFirst
                    >
                      <ThemeProvider theme={activeTheme}>
                        {messages != null ? (
                          <IntlProvider
                            locale={locale}
                            defaultLocale="en"
                            messages={messages}
                          >
                            <Switch>
                              {isAgentPortal && (
                                <Route path="*">
                                  <AgentBanner
                                    firstName={firstName}
                                    lastName={lastName}
                                  />
                                </Route>
                              )}
                              <Route path="*">
                                <RewardsBanner />
                              </Route>
                            </Switch>
                            <Body
                              sessionInfo={sessionInfo}
                              history={baseHistory}
                            />
                          </IntlProvider>
                        ) : (
                          <>{/* Loading */}</>
                        )}
                      </ThemeProvider>
                    </StylesProvider>
                  </div>
                </ClientContext.Provider>
              </Router>
            </PortalComponentProvider>
          </UserSourceProvider>
        </ExperimentsHookProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

export const Body = (props: {
  sessionInfo?: SessionInfo;
  history: H.History;
}) => {
  const { sessionInfo, history } = props;
  const dispatch = useDispatch();

  const expState = useExperiments();

  const StaysBookingExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_BOOKING_EXPERIMENT
  );
  const isStaysBookingEnabled = React.useMemo(
    () => StaysBookingExperiment === ExperimentVariant.AVAILABLE,
    [StaysBookingExperiment]
  );

  React.useEffect(() => {
    // Redirect to home page if vacation rentals booking is disabled
    if (expState.experiments.length > 0 && !isStaysBookingEnabled) {
      history.push(PATH_STAYS);
    }
  }, [expState, history]);

  useEffect(() => {
    if (sessionInfo) {
      dispatch(setIsFirstLaunch(sessionInfo.isFirstSession));
      dispatch(fetchRewardsAccounts(true, sessionInfo));
      dispatch(setUserInfo(sessionInfo.userInfo));
      if (sessionInfo.isDelegatedSession) {
        dispatch(setAgentEmail(sessionInfo.isDelegatedSession));
      }
    }
  }, [sessionInfo, dispatch]);

  useMedalliaFeedback();

  if (isStaysBookingEnabled) {
    return (
      <Box className="main-section">
        <Switch>
          <Route path={PATH_VACATION_RENTALS_SHOP}>
            <VacationRentalShop />
          </Route>
          <Route path={PATH_VACATION_RENTALS_BOOK}>
            <VacationRentalBook />
          </Route>
          <Route render={() => <Redirect to={PATH_STAYS} />} />
        </Switch>
      </Box>
    );
  }

  return null;
};

export default App;
